import premierMdLogo from "@/assets/premier-md-logo.svg";

export const NOTE_PREVIEW_EMR_LOGO = premierMdLogo;

export const NOTE_PREVIEW_LABELS = {
  dialogTitle: "Visit Note",
  dialogTitleSeparator: " — ",
  download: "Download",
  includeInReport: "Include in report",
  clearAll: "Clear all",
  selectAll: "Select all",
  noSections: "No sections to show.",
  defaultTitle: "Visit Note",
  defaultPracticeName: "eCareHealth",
  patientDetails: "Patient Details",
  name: "Name",
  dob: "DOB",
  mobile: "Mobile",
  patNumber: "Patient ID",
  address: "Address",
  renderingProvider: "Rendering Provider",
  location: "Location",
  dos: "DOS",
  signedDateTime: "Signed Date & Time",
  notSigned: "Not signed",
  notes: "Notes",
  scorePrefix: "Score ",
  screeningDateSeparator: " – ",
  draftWatermark: "DRAFT – Not signed",
  versionPrefix: "Version ",
  patNumberPrefix: "Patient ID ",
  pharmacyPhone: "Phone",
  pharmacyFax: "Fax",
  pharmacyAddress: "Address",
  patient: "Patient",
  helpLinePrefix:
    "If you have any questions or concerns regarding this documentation, please call ",
  defaultTemplateVersion: "1.0",
  emrLogoAlt: "Premier MD",
  loadError: "The note sections could not be loaded.",
  addendum: "Addendum",
  addedByPrefix: "Added by ",
  addedOnSeparator: " on ",
  cosignerNote: "Co-Signer's note",
  cosignedNote: "Co-Signed Note",
  cosignerNotePlaceholder:
    "Add a note to this visit note. It is saved when you co-sign.",
  cancel: "Cancel",
  editNote: "Edit Note",
  saveAndCoSign: "Save & Co-Sign",
  cosignDialogTitle: "Co-Sign Visit Note",
  signedByPrefix: "Signed by ",
  signedOnSeparator: " on ",
  enterCosignPin: "Enter Co-Sign PIN",
  forgotPin: "Forgot PIN?",
  verifyAndContinue: "Verify & Continue",
  cosignPinRequired: "Co-Sign PIN is required.",
  cosignPinTooShort: "Minimum 4 characters required.",
  cosignPinIncorrect: "Incorrect Co-Sign PIN.",
  coSignature: "Signature",
  importSignature: "Import Signature",
  importSignatureUnavailable:
    "Signature is not available for this provider. Please sign the signature manually to get import signature feature",
  clearSignature: "Clear",
  coSign: "Co-Sign",
  cosignSuccessPrefix: "Visit note for ",
  cosignSuccessSuffix:
    " co-signed and locked · moved to Billing > Ready to Bill",
} as const;

export const NOTE_PREVIEW_COSIGN_NOTE_ROWS = 4;
export const NOTE_PREVIEW_COSIGN_NOTE_FIELD = "cosignerNote";
export const NOTE_PREVIEW_COSIGN_HEADING_BACKGROUND = "#FBF3E4";
export const NOTE_PREVIEW_COSIGN_PIN_FIELD = "pin";
export const NOTE_PREVIEW_COSIGN_PIN_MIN_LENGTH = 4;
// Keeps the browser password manager from offering to save the PIN as a login credential.
export const NOTE_PREVIEW_COSIGN_PIN_AUTOCOMPLETE = "one-time-code";
export const NOTE_PREVIEW_COSIGN_PIN_DIALOG_WIDTH = "420px";
// Pixel sizes only: the canvas bitmap ignores percentages and strokes would fall outside it.
export const NOTE_PREVIEW_COSIGN_SIGNATURE_WIDTH = "560px";
export const NOTE_PREVIEW_COSIGN_SIGNATURE_HEIGHT = "240px";
export const NOTE_PREVIEW_COSIGN_BUTTON_HEIGHT = "30px";
export const COSIGN_STEP = {
  IDLE: "IDLE",
  PIN: "PIN",
  SIGN: "SIGN",
} as const;
export const NOTE_PREVIEW_COSIGN_DEFAULT_VALUES = { cosignerNote: "" } as const;

export const NOTE_PREVIEW_ADDENDUM_SECTION_KEY = "addendum";
export const NOTE_PREVIEW_COSIGN_SECTION_KEY = "cosign";

export const NOTE_PREVIEW_PDF_FILE_NAME = "visit-note.pdf";
export const NOTE_PREVIEW_PDF_FILE_EXTENSION = ".pdf";
export const NOTE_PREVIEW_PDF_FILE_SEPARATOR = "_";
export const NOTE_PREVIEW_PDF_FILE_DATE_FORMAT = "MM-DD-YYYY";
export const NOTE_PREVIEW_UNSAFE_FILE_CHARS = /[^A-Za-z0-9-]+/g;
export const NOTE_PREVIEW_HEADER_LINE_SEPARATOR = " · ";
export const NOTE_PREVIEW_WATERMARK_COLOR = "rgba(200, 0, 0, 0.14)";
export const NOTE_PREVIEW_WATERMARK_FONT_SIZE_PX = 72;
export const NOTE_PREVIEW_WATERMARK_ROTATION_DEG = -30;
export const NOTE_PREVIEW_DIALOG_WIDTH = "min(1280px, 95vw)";
export const NOTE_PREVIEW_SIDEBAR_WIDTH_PX = 250;
export const NOTE_PREVIEW_CANVAS_BACKGROUND = "#F4F6F9";
export const NOTE_PREVIEW_PAPER_PADDING_PX = 28;
export const NOTE_PREVIEW_BAR_BACKGROUND = "#EEF3FA";
export const NOTE_PREVIEW_PRIMARY_COLOR = "#1E88E5";
export const NOTE_PREVIEW_TEXT_COLOR = "#1A1A1A";
export const NOTE_PREVIEW_DIVIDER_COLOR = "#E0E0E0";
// Hidden until the backend drops them: Units, Modifier, Linked ICD-10 and DOS from Procedures, Seq from Diagnosis.
export const NOTE_PREVIEW_HIDDEN_TABLE_COLUMNS = [
  "Units",
  "Modifier",
  "Linked ICD-10",
  "DOS",
  "Seq",
] as const;
// Titles whose text is free prose; printed whole, never split on commas or dots.
export const NOTE_PREVIEW_FREE_TEXT_TITLES = [
  "Additional note",
  "Additional notes",
  "Additional details",
  "Notes",
] as const;
// Titles whose text is "name, phone, fax …, address"; rendered as a pharmacy card, one per sentence.
export const NOTE_PREVIEW_PHARMACY_TITLES = [
  "Preferred pharmacy",
  "Pharmacy",
] as const;
export const NOTE_PREVIEW_PHARMACY_PHONE_PATTERN =
  /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
export const NOTE_PREVIEW_PHARMACY_FAX_PREFIX = /^fax\s*:?\s*/i;
// Sections printed as plain paragraphs: any line title such as "Additional note" is dropped.
// Address parts are comma separated; the state id sits just before the zip code.
export const NOTE_PREVIEW_ADDRESS_SEPARATOR = ",";
export const NOTE_PREVIEW_STATE_ID_PATTERN = /^\d{1,2}$/;
export const NOTE_PREVIEW_ZIP_PATTERN = /^\d{5,6}(-\d{4})?$/;
export const NOTE_PREVIEW_PLAIN_TEXT_SECTION_KEYS = [
  "hpi",
  "chief_complaints",
] as const;
// Credential suffixes the backend folds into provider names ("Shelby MD, Thomas"); stripped for display.
// Provider names arrive as "Last, First"; the comma is dropped for display.
export const NOTE_PREVIEW_NAME_COMMA_PATTERN = /\s*,\s*/g;
export const NOTE_PREVIEW_PROVIDER_CREDENTIAL_PATTERN =
  /\s+(MD|DO|PA|PA-C|NP|FNP|DNP|APRN|RN|LPN|PhD|CRNP)\b\.?/gi;
export const NOTE_PREVIEW_BULLET_SEPARATOR = ",";
// Everything from this marker to the end of a line is one free-text bullet.
export const NOTE_PREVIEW_NOTE_MARKER = "Note: ";
// The backend labels our "Note: …" line as "Additional note"; the marker is dropped so the label is not doubled.
export const NOTE_PREVIEW_LEADING_NOTE_MARKER = /^note:\s*/i;
// A sentence this long inside a checkbox answer is explanatory prose and prints as a paragraph, not bullets.
export const NOTE_PREVIEW_PROSE_MIN_WORDS = 8;
export const NOTE_PREVIEW_INLINE_LABEL_SEPARATOR = ": ";
// Numbered chart entries such as "1. Condition": the entry value becomes the heading, nested lines its bullets.
export const NOTE_PREVIEW_NUMBERED_ENTRY_PATTERN = /^(\d+)\.\s+\S/;
export const NOTE_PREVIEW_TRAILING_DOT = /\.\s*$/;
// Separator punctuation left dangling once a "Note: …" tail is cut off.
export const NOTE_PREVIEW_TRAILING_SEPARATORS = /[\s,.]+$/;
export const NOTE_PREVIEW_SENTENCE_TERMINATOR = ".";
export const NOTE_PREVIEW_WHITESPACE = /\s/;
// "Label: value" at the start marks a row list such as "Last PCP visit: 09/2026. Last mammogram: normal".
export const NOTE_PREVIEW_LABEL_VALUE_PATTERN = /^[^:.]{1,60}:\s*\S/;
export const NOTE_PREVIEW_BULLET_OPEN_BRACKETS = "([{";
export const NOTE_PREVIEW_BULLET_CLOSE_BRACKETS = ")]}";
export const NOTE_PREVIEW_NESTED_INDENT_PX = 16;
export const NOTE_PREVIEW_GROUP_HEADING_FONT_SIZE_PX = 11;
// Leading yes/no the backend glued to its explanation ("yes Due to …"); split so the answer is a bullet and the rest a paragraph.
export const NOTE_PREVIEW_LEADING_ANSWER_PATTERN = /^(yes|no)\s+(?=\S)/i;
export const NOTE_PREVIEW_TABLE_BORDER_COLOR = "#D9D9D9";
// Sections whose numbered lines are sub-sections ("1. Smoker") with selections, details and notes nested under them.
export const NOTE_PREVIEW_HIERARCHY_SECTION_KEYS = ["social_history"] as const;
// Sections whose findings stay comma separated on one bullet per body system, matched by key or title.
export const NOTE_PREVIEW_COMMA_JOINED_SECTIONS = [
  "review_of_systems",
  "physical_exam",
  "Review of Systems",
  "Physical Exam",
] as const;
// Sections whose titled, non-note lines are completed screeners; they print under this heading as on the note screen.
export const NOTE_PREVIEW_SCREENING_SECTION_KEY = "screening";
// The Procedures (CPT) section, with the CPT lines stored under Plan of Care, prints last as "Procedure Codes".
export const NOTE_PREVIEW_PLAN_OF_CARE_SECTION_KEY = "plan_of_care";
export const NOTE_PREVIEW_PROCEDURES_SECTION_PATTERN = /procedure|cpt/i;
export const NOTE_PREVIEW_PROCEDURE_CODES_SECTION_KEY = "procedure_codes";
export const NOTE_PREVIEW_PROCEDURE_CODES_TITLE = "Procedure Codes";
export const NOTE_PREVIEW_SCREENING_HEADING = "Screening Assessments";
export const NOTE_PREVIEW_NUMBER_PREFIX_PATTERN = /^\d+\.\s*/;
export const NOTE_PREVIEW_TABLE_HEADER_BACKGROUND = "#F2F2F2";
export const NOTE_PREVIEW_MUTED_COLOR = "#6B7280";
export const NOTE_PREVIEW_LOGO_SIZE_PX = 76;
// Patient grid: two equal columns with a fixed label column so the colons line up.
export const NOTE_PREVIEW_DETAIL_LABEL_WIDTH_PX = 120;
export const NOTE_PREVIEW_SIGNATURE_HEIGHT_PX = 56;
export const NOTE_PREVIEW_CLOCK_DRAWING_SECTION_KEY = "mini_cognitive_test";
export const NOTE_PREVIEW_CLOCK_DRAWING_HEIGHT_PX = 220;
export const NOTE_PREVIEW_FOOTER_LOGO_HEIGHT_PX = 32;
export const NOTE_PREVIEW_BODY_FONT_SIZE_PX = 12;
export const NOTE_PREVIEW_GROUP_ORDER = [
  "Subjective",
  "Objective",
  "Assessment",
  "Plan",
] as const;
